import React from 'react';
import { HOST_VOICE_OPTIONS, HOST_VOICE_DEFAULTS } from '../../constants/hostVoiceOptions';

const HOSTS = ['Maya', 'Daniel'];
const NATIVE_LANG = 'en-US';

const selectStyle = {
  width: '100%', padding: '0.5rem', borderRadius: '6px',
  border: '1px solid #cbd5e0', fontSize: '0.9rem', background: 'white',
};

/**
 * Per-host voice pickers for the conversational format.
 * @param value             { Maya:{native,secondary}, Daniel:{native,secondary} } | null
 * @param onChange          (hostVoices) => void
 * @param secondaryLanguage e.g. 'es-ES' / 'zh-CN' — the source's translationLanguage, or null
 * @param needsSecondary    true when the mode speaks the second language (not English-only)
 * @param languageMode      'secondary-only' | 'native-then-secondary' | 'native-only'
 */
export default function HostVoicePicker({ value, onChange, secondaryLanguage, needsSecondary, languageMode }) {
  const nativeOpts = HOST_VOICE_OPTIONS[NATIVE_LANG] || [];
  const showNative = languageMode !== 'secondary-only';
  const showSecondary = !!(needsSecondary && secondaryLanguage);
  const secondaryOpts = showSecondary ? HOST_VOICE_OPTIONS[secondaryLanguage] : null;
  // Only render the second-language pickers when we actually have options for that language;
  // otherwise the scheduler falls back to sensible defaults and we show a note. This guard is
  // what keeps an unsupported language (e.g. ja-JP) from crashing the whole schedule form.
  const canPickSecondary = showSecondary && Array.isArray(secondaryOpts) && secondaryOpts.length > 0;

  const effective = (host, side, lang) => {
    const custom = value && value[host] && value[host][side];
    if (custom) return custom;
    return (HOST_VOICE_DEFAULTS[lang] || {})[host] || '';
  };

  const setVoice = (host, side, id) => {
    const next = {};
    HOSTS.forEach((h) => {
      next[h] = { ...((value && value[h]) || {}) };
      if (showNative) {
        next[h].native = (h === host && side === 'native') ? id : effective(h, 'native', NATIVE_LANG);
      }
      if (canPickSecondary) {
        next[h].secondary = (h === host && side === 'secondary') ? id : effective(h, 'secondary', secondaryLanguage);
      }
    });
    onChange(next);
  };

  const clash = (side, lang) => effective('Maya', side, lang) === effective('Daniel', side, lang);

  const renderSelect = (host, side, lang, opts) => (
    <select style={selectStyle} value={effective(host, side, lang)} onChange={(e) => setVoice(host, side, e.target.value)}>
      {(opts || []).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
    </select>
  );

  if (!showNative && !showSecondary) return null;

  return (
    <div style={{ marginTop: '1rem', padding: '1rem', background: '#f7fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
      <div style={{ fontWeight: 600, color: '#2d3748', marginBottom: '0.75rem' }}>🎙️ Host voices</div>
      <div style={{ display: 'grid', gridTemplateColumns: (showNative && canPickSecondary) ? '90px 1fr 1fr' : '90px 1fr', gap: '0.5rem 0.75rem', alignItems: 'center' }}>
        <div />
        {showNative && <div style={{ fontSize: '0.8rem', color: '#718096' }}>English</div>}
        {canPickSecondary && <div style={{ fontSize: '0.8rem', color: '#718096' }}>Second language</div>}
        {HOSTS.map((host) => (
          <React.Fragment key={host}>
            <div style={{ fontWeight: 500, color: '#4a5568', fontSize: '0.9rem' }}>{host}</div>
            {showNative && renderSelect(host, 'native', NATIVE_LANG, nativeOpts)}
            {canPickSecondary && renderSelect(host, 'secondary', secondaryLanguage, secondaryOpts)}
          </React.Fragment>
        ))}
      </div>
      {((showNative && clash('native', NATIVE_LANG)) || (canPickSecondary && clash('secondary', secondaryLanguage))) && (
        <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#c05621' }}>
          ⚠️ Maya and Daniel share a voice — pick different voices so listeners can tell them apart.
        </p>
      )}
      {showSecondary && !canPickSecondary && (
        <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#718096' }}>
          Voice choices for the second language aren't available yet — sensible default voices will be used.
        </p>
      )}
    </div>
  );
}
